import { useState } from 'react';
import { useExports } from '../hooks/useExports';
import { Table } from '../components/ui/Table';
import { Button } from '../components/ui/Button';
import { Card, CardHeader } from '../components/ui/Card';
import { DocumentArrowDownIcon, FolderIcon } from '@heroicons/react/24/outline';

interface ExportFile {
  key: string;
  size: number;
  uploaded: string;
}

const prefixes = [
  { value: '', label: 'All Files' },
  { value: 'exports/outshipments/', label: 'Outshipments' },
  { value: 'exports/transfers/', label: 'Transfers' },
  { value: 'exports/new-accounts/', label: 'New Accounts' },
  { value: 'results/transfers/', label: 'Transfer Results' },
  { value: 'results/outshipments/', label: 'Outshipment Results' },
  { value: 'results/new-accounts/', label: 'New Account Results' },
];

export function ExportsPage() {
  const [prefix, setPrefix] = useState('');
  const { data: files, isLoading } = useExports(prefix || undefined);

  function formatSize(bytes: number) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  function handleDownload(key: string) {
    window.open(`/api/exports/${encodeURIComponent(key)}/download`, '_blank');
  }

  return (
    <div className="space-y-6">
      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2">
        {prefixes.map((p) => (
          <button
            key={p.value}
            onClick={() => setPrefix(p.value)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors
              ${prefix === p.value
                ? 'bg-gold-600 text-white'
                : 'bg-silver-100 text-silver-600 hover:bg-silver-200'
              }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <Card padding={false}>
        <div className="p-6 pb-0">
          <CardHeader
            title="Archived Files"
            description="Excel files generated and downloaded from FideliTrade SFTP"
          />
        </div>
        <Table<ExportFile>
          loading={isLoading}
          data={(files ?? []) as ExportFile[]}
          columns={[
            {
              key: 'key', header: 'File',
              render: (r) => {
                const name = r.key.split('/').pop() ?? r.key;
                return (
                  <div className="flex items-center gap-2">
                    <FolderIcon className="h-4 w-4 text-gold-500" />
                    <div>
                      <p className="font-medium text-silver-800">{name}</p>
                      <p className="text-xs text-silver-400">{r.key}</p>
                    </div>
                  </div>
                );
              },
            },
            { key: 'size', header: 'Size', render: (r) => formatSize(r.size) },
            {
              key: 'uploaded', header: 'Uploaded',
              render: (r) => r.uploaded ? new Date(r.uploaded).toLocaleString() : '-',
            },
            {
              key: 'download', header: '',
              render: (r) => (
                <Button variant="ghost" size="sm" onClick={() => handleDownload(r.key)}>
                  <DocumentArrowDownIcon className="h-4 w-4" /> Download
                </Button>
              ),
            },
          ]}
          emptyMessage="No files found for this category"
        />
      </Card>
    </div>
  );
}
